import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
  SortingState,
} from "@/lib/Components/DataTable/DataTable";
import {
  CallbackLeadListItem,
  callbackLeadListQuery,
} from "@/app/CallbackLeads/GraphQL/callbackLeadListQuery";
import { BookingFindRelocationDialog } from "@/app/Bookings/Components/BookingFindRelocationDialog";
import { CallbackLeadOutcomeDialog } from "@/app/CallbackLeads/Components/CallbackLeadOutcomeDialog";
import { CallbackLeadChangeRelocationDialog } from "@/app/CallbackLeads/Components/CallbackLeadChangeRelocationDialog";
import { CallbackLeadTakePaymentDialog } from "@/app/CallbackLeads/Components/CallbackLeadTakePaymentDialog";
import { CallbackLeadCreateDialog } from "@/app/CallbackLeads/Components/CallbackLeadCreateDialog";
import { UserWaitlistsDialog } from "@/app/Waitlists/Components/UserWaitlistsDialog";
import { CallbackLeadList } from "@/app/CallbackLeads/Routes/callbackLeadRoutes";
import { CallZoneFilter } from "@/app/CallbackLeads/Components/CallZoneFilter";
import { CallbackLeadSourceFilter } from "@/app/CallbackLeads/Components/CallbackLeadSourceFilter";
import {
  CallZone,
  getCallZoneRegions,
} from "@/app/CallbackLeads/Utils/callZones";
import {
  dismissCallbackLeadMutation,
  startCallbackLeadCallMutation,
} from "@/app/CallbackLeads/GraphQL/callbackLeadMutations";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  CallbackLeadListQueryVariables,
  CallbackLeadSource,
  CallbackLeadStatus,
  OrderByRelationWithColumnAggregateFunction,
  QueryCallbackLeadsOrderByBookingColumn,
  QueryCallbackLeadsOrderByColumn,
  QueryCallbackLeadsOrderByRelationOrderByClause,
  SortOrder,
  State,
} from "@/gql/graphql";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { Button } from "@/components/catalyst/button";
import { Link } from "@/components/catalyst/link";
import { Text } from "@/components/catalyst/text";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import { CopyToClipboard } from "@/lib/Components/Common/CopyToClipboard";
import { formatDate } from "@/lib/Formatters/formatDate";
import { formatDateTimeFromNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import {
  ArrowTopRightOnSquareIcon,
  BellAlertIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid";
import { PhoneArrowUpRightIcon } from "@heroicons/react/20/solid";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslations } from "use-intl";
import { useState } from "react";
import { useNow } from "@/lib/Hooks/useNow";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";

type CallbackLeadColumnId =
  | "customer"
  | "deal"
  | "localTime"
  | "calls"
  | "type"
  | "note"
  | "actions";

const sourceColors: Record<CallbackLeadSource, BadgeColor> = {
  [CallbackLeadSource.IncompleteBooking]: "amber",
  [CallbackLeadSource.Waitlist]: "blue",
  [CallbackLeadSource.SupplierCancellation]: "red",
  [CallbackLeadSource.Manual]: "zinc",
};

const listStatuses: Record<CallbackLeadList, CallbackLeadStatus[] | undefined> =
  {
    due: [CallbackLeadStatus.Pending],
    snoozed: [CallbackLeadStatus.Snoozed],
    closed: [CallbackLeadStatus.Closed],
    all: undefined,
  };

/**
 * Nothing is sorted by hand to begin with: the list arrives with abandoned
 * checkouts first and the freshest lead of each kind at the top. Sorting a
 * column takes over from that.
 */
const listSorting: Record<
  CallbackLeadList,
  SortingState<CallbackLeadColumnId>
> = {
  due: [],
  all: [],
  snoozed: [],
  closed: [],
};

const listOrderBy: Record<
  CallbackLeadList,
  QueryCallbackLeadsOrderByRelationOrderByClause[]
> = {
  due: [
    {
      order: SortOrder.Desc,
      column: QueryCallbackLeadsOrderByColumn.BecameCallableAt,
    },
  ],
  all: [
    {
      order: SortOrder.Desc,
      column: QueryCallbackLeadsOrderByColumn.BecameCallableAt,
    },
  ],
  snoozed: [
    {
      order: SortOrder.Asc,
      column: QueryCallbackLeadsOrderByColumn.SnoozedUntil,
    },
  ],
  closed: [
    {
      order: SortOrder.Desc,
      column: QueryCallbackLeadsOrderByColumn.CreatedAt,
    },
  ],
};

/**
 * Zoom reports a call once it is over, so the newest dial is the last thing
 * that happened on this lead.
 */
function getLastCall(lead: CallbackLeadListItem) {
  if (!lead.calls.length) {
    return null;
  }

  return lead.calls.reduce((latest, call) =>
    dayjs(call.dialled_at).isAfter(latest.dialled_at) ? call : latest,
  );
}

type CallbackLeadWaitlist = NonNullable<CallbackLeadListItem["waitlist"]>;

/**
 * An abandoned checkout is hung on whatever open lead the customer already
 * has, so a waitlist lead can carry a booking that has nothing to do with why
 * it was raised. The source says what the rep is ringing about.
 */
function getWaitlist(lead: CallbackLeadListItem): CallbackLeadWaitlist | null {
  return lead.source === CallbackLeadSource.Waitlist ? lead.waitlist : null;
}

export function CallbackLeadTable({ list }: { list: CallbackLeadList }) {
  const t = useTranslations("callbackLead");
  const [zones, setZones] = useState<CallZone[]>([]);
  // Abandoned checkouts and cancelled bookings are the reason this list exists,
  // so the rep lands on them and widens the filter themselves.
  const [sources, setSources] = useState<CallbackLeadSource[]>([
    CallbackLeadSource.SupplierCancellation,
    CallbackLeadSource.IncompleteBooking,
  ]);

  const columns: DataTableColDef<
    CallbackLeadListItem,
    any,
    CallbackLeadColumnId
  >[] = [
    {
      id: "customer",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("table.customer")} />
      ),
      accessorFn: (row) => row.name,
      size: 240,
      cell: ({ row }) => {
        const lead = row.original;
        const waitlist = getWaitlist(lead);

        return (
          <div className="flex flex-col gap-y-0.5">
            {waitlist && lead.user ? (
              <Link
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to={"/$tenant/$orgId/supplier/{-$supplierId}/users/$id"}
                params={(prev) => ({ ...prev, id: lead.user!.id })}
                viewTransition
                className="font-medium text-blue-500 underline"
              >
                {lead.name}
              </Link>
            ) : lead.booking ? (
              <Link
                to={
                  "/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"
                }
                params={(prev) => ({ ...prev, bookingId: lead.booking!.id })}
                viewTransition
                className="font-medium text-blue-500 underline"
              >
                {lead.name}
              </Link>
            ) : (
              <span className="font-medium">{lead.name}</span>
            )}
            <CopyToClipboard
              text={lead.phone}
              title={t("actions.copyPhone")}
              className="text-xs text-gray-500 hover:underline"
            />
            {lead.email ? (
              <CopyToClipboard
                text={lead.email}
                title={t("actions.copyEmail")}
                className="text-xs text-gray-500 hover:underline"
              />
            ) : null}
          </div>
        );
      },
    },
    {
      id: "deal",
      header: t("table.deal"),
      enableSorting: false,
      size: 260,
      cell: ({ row }) => {
        const lead = row.original;
        const waitlist = getWaitlist(lead);

        // What the rep talks about: what the customer asked to hear about, the
        // deal they abandoned, or the one that matched. A reminder has none.
        if (waitlist) {
          return <WaitlistCell waitlist={waitlist} />;
        }

        if (
          lead.source === CallbackLeadSource.SupplierCancellation &&
          lead.relocation
        ) {
          return (
            <DealCell
              route={`${lead.relocation.departureCity.name} → ${lead.relocation.deliveryCity.name}`}
              reference={lead.relocation.reference}
              date={lead.relocation.available_from_date}
              createdAt={lead.relocation.created_at}
              url={lead.relocation.customer_url}
            />
          );
        }

        if (lead.booking) {
          return (
            <DealCell
              route={`${lead.booking.departureOffice.name} → ${lead.booking.deliveryOffice.name}`}
              reference={lead.booking.reference}
              date={lead.booking.depart_at}
              createdAt={lead.booking.created_at}
              url={lead.booking.relocation.customer_url}
              otherCount={lead.bookings_count - 1}
            />
          );
        }

        if (lead.relocation) {
          return (
            <DealCell
              route={`${lead.relocation.departureCity.name} → ${lead.relocation.deliveryCity.name}`}
              reference={lead.relocation.reference}
              date={lead.relocation.available_from_date}
              createdAt={lead.relocation.created_at}
              url={lead.relocation.customer_url}
            />
          );
        }

        return <Text className="text-gray-500">{t("table.reminder")}</Text>;
      },
    },
    {
      id: "localTime",
      header: t("table.localTime"),
      enableSorting: false,
      cell: ({ row }) => {
        const waitlist = getWaitlist(row.original);
        const timezone = waitlist
          ? (row.original.relocation?.departureCity.timezone ??
            waitlist.departureCity?.timezone)
          : (row.original.booking?.departureOffice.city.timezone ??
            row.original.relocation?.departureCity.timezone);

        if (!timezone) {
          return <Text className="text-gray-500">--</Text>;
        }

        return <LocalTime timezone={timezone} />;
      },
    },
    {
      id: "calls",
      header: t("table.calls"),
      enableSorting: false,
      size: 140,
      cell: ({ row }) => {
        const lead = row.original;
        const lastCall = getLastCall(lead);
        // The rep's own words on the last call beat what Zoom made of it, and
        // a missed dial writes its own outcome, so only a connected call the
        // rep has not logged yet falls back to the Zoom result.
        const detail = lead.outcome
          ? t(`outcome.${lead.outcome}`)
          : lastCall?.result
            ? t(`callResult.${lastCall.result}`)
            : null;

        return (
          <div className="flex flex-col items-start gap-y-0.5">
            {lastCall ? (
              <>
                <span className="text-sm font-medium">{lead.calls_count}</span>
                <span className="text-xs text-gray-500">
                  {formatDateTimeFromNow(lastCall.dialled_at)}
                </span>
              </>
            ) : (
              <span className="text-xs text-gray-500">
                {t("table.noCalls")}
              </span>
            )}
            {detail ? (
              <span className="text-xs text-gray-500">{detail}</span>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "type",
      header: t("table.type"),
      enableSorting: false,
      size: 140,
      accessorFn: (row) => row.source,
      cell: ({ row }) => (
        <Badge color={sourceColors[row.original.source]}>
          {t(`source.${row.original.source}`)}
        </Badge>
      ),
    },
    {
      id: "note",
      header: t("table.note"),
      enableSorting: false,
      isInitiallyHidden: true,
      accessorFn: (row) => row.note,
      cell: DeEmphasisedCell,
    },
    {
      id: "actions",
      header: () => null,
      enableSorting: false,
      size: 220,
      cell: ({ row }) => <CallbackLeadQuickActions lead={row.original} />,
    },
  ];

  return (
    <DataTable
      id="callback-leads"
      document={callbackLeadListQuery}
      columns={columns}
      accessor={(data) => data.callbackLeads}
      initialSorting={listSorting[list]}
      searchPlaceholder={`${t("table.customer")}...`}
      rightButtons={<CreateCallbackLeadButton />}
      filters={(table) => {
        const resetPage = () =>
          table.setPagination((prev) => ({
            ...prev,
            pageIndex: 0,
          }));

        return (
          <>
            <CallbackLeadSourceFilter
              selected={sources}
              onChange={(newSources) => {
                setSources(newSources);
                resetPage();
              }}
            />
            <CallZoneFilter
              selected={zones}
              onChange={(newZones) => {
                setZones(newZones);
                resetPage();
              }}
            />
          </>
        );
      }}
      getQueryVariables={({ pagination, sorting, search }) => {
        const orderBy = sorting
          .map((sort) => {
            const order = sort.desc ? SortOrder.Desc : SortOrder.Asc;

            if (sort.id === "customer") {
              return {
                order,
                booking: {
                  column: QueryCallbackLeadsOrderByBookingColumn.Name,
                  aggregate: OrderByRelationWithColumnAggregateFunction.Max,
                },
              } satisfies QueryCallbackLeadsOrderByRelationOrderByClause;
            }

            return null;
          })
          .filter((value) => value !== null);

        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          search: search ? search : undefined,
          status: listStatuses[list],
          source: sources.length ? sources : undefined,
          regions: zones.length ? getCallZoneRegions(zones) : undefined,
          // A customer who reached checkout is the warmest lead on the list, so
          // they lead it until someone sorts a column for themselves.
          abandonedFirst: orderBy.length === 0,
          orderBy: orderBy.length ? orderBy : listOrderBy[list],
        } satisfies CallbackLeadListQueryVariables;
      }}
    />
  );
}

function WaitlistCell({ waitlist }: { waitlist: CallbackLeadWaitlist }) {
  const t = useTranslations("callbackLead");
  const tCity = useTranslations("city");
  const tWaitlist = useTranslations("waitlist");

  const place = (
    city: { name: string } | null | undefined,
    state: State | null | undefined,
  ) =>
    city ? city.name : state ? tCity(`state.${state}`) : t("table.anywhere");

  return (
    <div className="flex flex-col gap-y-0.5">
      <div className="flex items-center gap-x-1">
        <Text>
          {place(waitlist.departureCity, waitlist.departure_state)} →{" "}
          {place(waitlist.deliveryCity, waitlist.delivery_state)}
        </Text>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={waitlist.relocationLink}
              target="_blank"
              rel="noreferrer"
              aria-label={t("actions.viewDeals")}
              className="text-blue-500 hover:text-blue-600"
            >
              <ArrowTopRightOnSquareIcon className="size-3.5" />
            </a>
          </TooltipTrigger>
          <TooltipContent>{t("actions.viewDeals")}</TooltipContent>
        </Tooltip>
      </div>
      <span className="text-xs text-gray-500">
        {formatDate(waitlist.earliest_departure_date)} →{" "}
        {formatDate(waitlist.latest_departure_date)}
      </span>
      <span className="text-xs text-gray-500">
        {t("table.waitlistCreated", {
          when: formatDateTimeFromNow(waitlist.created_at),
        })}
        {waitlist.vehicle_category
          ? ` · ${tWaitlist(`waitlist_preference.vehicle_category.${waitlist.vehicle_category}`)}`
          : null}
        {waitlist.number_of_travellers
          ? ` · ${t("table.travellers", { count: waitlist.number_of_travellers })}`
          : null}
      </span>
    </div>
  );
}

function DealCell({
  route,
  reference,
  date,
  createdAt,
  url,
  otherCount = 0,
}: {
  route: string;
  reference: string;
  date: string;
  createdAt: string;
  url: string;
  otherCount?: number;
}) {
  const t = useTranslations("callbackLead");

  return (
    <div className="flex flex-col gap-y-0.5">
      <div className="flex items-center gap-x-1">
        <Text>{route}</Text>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              aria-label={t("actions.viewDeal")}
              className="text-blue-500 hover:text-blue-600"
            >
              <ArrowTopRightOnSquareIcon className="size-3.5" />
            </a>
          </TooltipTrigger>
          <TooltipContent>{t("actions.viewDeal")}</TooltipContent>
        </Tooltip>
      </div>
      <span className="text-xs text-gray-500">
        {reference} · {formatDate(date)} · {formatDateTimeFromNow(createdAt)}
      </span>
      {otherCount > 0 ? (
        <span className="text-xs text-gray-500">
          {t("table.otherIncomplete", { count: otherCount })}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Hours a customer can reasonably be rung, in their own time.
 */
const CALLABLE_FROM_HOUR = 8;
const CALLABLE_UNTIL_HOUR = 20;

function LocalTime({ timezone }: { timezone: string }) {
  const t = useTranslations("callbackLead");
  const now = useNow();
  const local = dayjs(now).tz(timezone);
  const hour = local.hour();
  const isCallable = hour >= CALLABLE_FROM_HOUR && hour < CALLABLE_UNTIL_HOUR;

  return (
    <Tooltip>
      <TooltipTrigger>
        <div className="flex flex-col items-start gap-y-0.5">
          <span
            className={cn(
              "font-medium tabular-nums",
              isCallable ? "text-gray-900" : "text-red-600",
            )}
          >
            {local.format("HH:mm")}
          </span>
          <span className="text-xs text-gray-500">{local.format("ddd")}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        {isCallable ? timezone : t("table.outsideCallingHours")}
      </TooltipContent>
    </Tooltip>
  );
}

function CreateCallbackLeadButton() {
  const t = useTranslations("callbackLead");
  const { open } = useDialog(CallbackLeadCreateDialog);

  return (
    <Button onClick={() => open({})}>
      <PlusIcon />
      {t("createDialog.open")}
    </Button>
  );
}

/**
 * Zoom Phone has no API for placing a call, so the backend records the call
 * first — which is what lets Zoom's call log be tied back to this lead — and
 * hands back the URI that makes the rep's own Zoom client dial.
 */
function DialButton({ lead }: { lead: CallbackLeadListItem }) {
  const t = useTranslations("callbackLead");
  const { mutateAsync: startCall, isPending } = useGqlMutation(
    startCallbackLeadCallMutation,
  );

  async function handleCall() {
    const { startCallbackLeadCall } = await startCall({ id: lead.id });

    window.location.href = startCallbackLeadCall.dial_url;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          color="green"
          isLoading={isPending}
          aria-label={t("actions.call")}
          onClick={handleCall}
        >
          <PhoneArrowUpRightIcon />
          {t("actions.call")}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{t("actions.callDescription")}</TooltipContent>
    </Tooltip>
  );
}

function CallbackLeadQuickActions({ lead }: { lead: CallbackLeadListItem }) {
  const t = useTranslations("callbackLead");
  const { open: openOutcome } = useDialog(CallbackLeadOutcomeDialog);
  const { open: openTakePayment } = useDialog(CallbackLeadTakePaymentDialog);
  const { open: openChangeRelocation } = useDialog(
    CallbackLeadChangeRelocationDialog,
  );
  const { open: openWaitlists } = useDialog(UserWaitlistsDialog);
  const { open: openFindRelocation } = useDialog(BookingFindRelocationDialog);
  const { mutate: dismiss, isPending } = useGqlMutation(
    dismissCallbackLeadMutation,
  );
  const waitlist = getWaitlist(lead);

  if (lead.status === CallbackLeadStatus.Closed) {
    return null;
  }

  return (
    <div className="flex justify-end gap-x-2">
      {waitlist && lead.user ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              plain
              aria-label={t("actions.manageWaitlists")}
              onClick={() => openWaitlists({ userId: lead.user!.id })}
            >
              <BellAlertIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("actions.manageWaitlists")}</TooltipContent>
        </Tooltip>
      ) : null}
      {lead.booking &&
      lead.source === CallbackLeadSource.SupplierCancellation ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              plain
              aria-label={t("actions.findSimilarRelocations")}
              onClick={() =>
                openFindRelocation({ bookingId: lead.booking!.id })
              }
            >
              <MagnifyingGlassIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("actions.findSimilarRelocations")}</TooltipContent>
        </Tooltip>
      ) : null}
      <DialButton lead={lead} />
      <Button
        outline
        onClick={() =>
          openOutcome({
            lead,
            onTakePayment:
              lead.booking &&
              lead.source !== CallbackLeadSource.SupplierCancellation
                ? () => openTakePayment({ bookingId: lead.booking!.id })
                : undefined,
            onChangeRelocation:
              lead.booking &&
              lead.relocation &&
              lead.source === CallbackLeadSource.SupplierCancellation
                ? () =>
                    openChangeRelocation({
                      bookingId: lead.booking!.id,
                      relocationId: lead.relocation!.id,
                    })
                : undefined,
          })
        }
      >
        {t("actions.logOutcome")}
      </Button>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            plain
            isLoading={isPending}
            aria-label={t("actions.close")}
            onClick={() => dismiss({ id: lead.id })}
          >
            <XMarkIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("actions.close")}</TooltipContent>
      </Tooltip>
    </div>
  );
}
